import React from 'react';
import Icon from 'src/components/common/Icon';
import { PageButton, PagerBar, PagerInfo, PagerNav } from './VisibilityAssignment.styles';

interface PaginationProps {
    /** Página actual (1-indexed). */
    page: number;
    /** Total de elementos (no de páginas). */
    total: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    /** Si se pasa, muestra el selector "Mostrar N". */
    pageSizeOptions?: number[];
    onPageSizeChange?: (size: number) => void;
    dataId?: string;
}

/** Lista acotada de números de página alrededor de la actual (máx. 5). */
const buildWindow = (page: number, pageCount: number, size = 5): number[] => {
    if (pageCount <= size) {
        return Array.from({ length: pageCount }, (_, i) => i + 1);
    }
    const half = Math.floor(size / 2);
    let start = Math.max(1, page - half);
    const end = Math.min(pageCount, start + size - 1);
    start = Math.max(1, end - size + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

const Pagination = ({
    page,
    total,
    pageSize,
    onPageChange,
    pageSizeOptions,
    onPageSizeChange,
    dataId = 'pagination',
}: PaginationProps) => {
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    if (pageCount <= 1 && !pageSizeOptions) return null;

    const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(total, page * pageSize);
    const pages = buildWindow(page, pageCount);

    return (
        <PagerBar data-id={dataId}>
            <PagerInfo>
                {pageSizeOptions && onPageSizeChange && (
                    <>
                        Mostrar
                        <select
                            data-id={`${dataId}-size`}
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        >
                            {pageSizeOptions.map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                    </>
                )}
                <span data-id={`${dataId}-range`}>
                    {from}–{to} de {total}
                </span>
            </PagerInfo>

            <PagerNav>
                <PageButton
                    type="button"
                    data-id={`${dataId}-prev`}
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    aria-label="Página anterior"
                >
                    <Icon icon="pcom-icon-chevron-left" size={12} />
                </PageButton>
                {pages.map((p) => (
                    <PageButton
                        key={p}
                        type="button"
                        data-id={`${dataId}-page-${p}`}
                        $active={p === page}
                        onClick={() => onPageChange(p)}
                    >
                        {p}
                    </PageButton>
                ))}
                <PageButton
                    type="button"
                    data-id={`${dataId}-next`}
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= pageCount}
                    aria-label="Página siguiente"
                >
                    <Icon icon="pcom-icon-chevron-right" size={12} />
                </PageButton>
            </PagerNav>
        </PagerBar>
    );
};

export default Pagination;
